import { useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import NetworkTopology from '../components/NetworkTopology';
import { WIDGET_REGISTRY } from '../components/widgets/WidgetRegistry';

// Widget ID → page mapping for click navigation
const WIDGET_PAGE_MAP = {
  assets: 'assets',
  vulns:  'vulns',
  risk:   'assets',
  ato:    'ato',
  ise:    'c2c',
  siem:   'alerts',
  fw:     'alerts',
};

// Expanded widget (2-col grid)
function ExpandedWidget({ id, title, children, onNavigate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const page = WIDGET_PAGE_MAP[id];
  return (
    <Paper
      ref={setNodeRef}
      onClick={() => !isDragging && page && onNavigate(page)}
      sx={{
        backgroundColor: '#111827',
        border: '1px solid #1e3a5f',
        borderRadius: 2,
        position: 'relative',
        height: '100%',
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        cursor: page ? 'pointer' : 'default',
        '&:hover': page ? { border: '1px solid #00bcd4' } : {},
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #1e3a5f' }}>
        <Typography variant="subtitle1" sx={{ color: '#ffffff', fontWeight: 700 }}>{title}</Typography>
        <Box {...attributes} {...listeners} onClick={(e) => e.stopPropagation()} sx={{ color: '#1e3a5f', cursor: 'grab', '&:hover': { color: '#00bcd4' } }}>
          <DragIndicatorIcon fontSize="small" />
        </Box>
      </Box>
      <Box sx={{ padding: 1 }}>{children}</Box>
    </Paper>
  );
}

export default function Dashboard({ systemId, setActivePage, onSelectDevice, onSelectGroup }) {
  const [widgets, setWidgets] = useState(['assets', 'risk', 'vulns', 'siem', 'ise', 'fw']);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragStart = ({ active }) => setActiveId(active.id);

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    setWidgets((prev) => arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id)));
  };

  const activeWidget = activeId ? WIDGET_REGISTRY[activeId] : null;

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3, display: 'flex', gap: 3, alignItems: 'stretch' }}>

      {/* Left — Network Topology */}
      <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: 3, overflow: 'hidden', width: 680, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, marginBottom: 2 }}>Network Topology</Typography>
        <Box sx={{ flex: 1, overflow: 'hidden', borderRadius: 1 }}>
          <NetworkTopology onSelectDevice={onSelectDevice} onSelectGroup={onSelectGroup} height="100%" />
        </Box>
      </Paper>

      {/* Right — 2-column widget grid */}
      <Box sx={{ flex: 1 }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <SortableContext items={widgets} strategy={rectSortingStrategy}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {widgets.map((id) => {
                const w = WIDGET_REGISTRY[id];
                const exp = typeof w.expanded === 'function' ? w.expanded(systemId) : w.expanded;
                return (
                  <ExpandedWidget key={id} id={id} title={exp.title} onNavigate={setActivePage}>
                    {exp.content}
                  </ExpandedWidget>
                );
              })}
            </Box>
          </SortableContext>

          <DragOverlay>
            {activeWidget && (
              <Paper sx={{ backgroundColor: '#111827', border: '1px solid #00bcd4', borderRadius: 2, padding: 2, opacity: 0.9 }}>
                <Typography variant="body2" sx={{ color: '#00bcd4' }}>{typeof activeWidget.expanded === 'function' ? activeWidget.expanded(systemId).title : activeWidget.expanded.title}</Typography>
              </Paper>
            )}
          </DragOverlay>
        </DndContext>
      </Box>

    </Box>
  );
}
